import { spaceApiCompraInteligente } from "../clienteSpace";
import type {
	EAnaliseEstoqueRequestFieldKey,
	IAnaliseEstoqueRawRow,
	IDownloadExportExcelPayload,
	IGerarExportExcelPayload,
	IGerarExportExcelResponse,
	IConsultarMetricasPayload,
	IInventoryAnalysisPayload,
	ITopProdutosPayload,
	IOTBAgrupamentoValor,
	IOTBGradeProdutoResponse,
	IResumoOTBPayload,
	IOTBGradeResumidaPayload,
	IOTBGradeResumidaResponse,
	IProductDistributionColorItemResponse,
	IProductDistributionColorsPayload,
	IProductDistributionPayload,
	IProductDistributionViewResponse,
	IQueryHistoryItemResponse,
	IResumoOTBResponse,
	IVitrineAgrupamentoItem,
	IVitrineAgrupamentoPayload,
	IVitrineAgrupamentoResponse,
	IRespostaMetricasDashboard,
	IProductInfoPayload,
	IProductInfoResponse,
} from "../dtos/compra-inteligente.dto";
import { getResumoProduto } from "./estoque-service.spaceAPI";
import { getStatusError } from "@/utils/utils";
import { normalizarFaixaPrecoValorVitrine, normalizarRespostaVitrine } from "./utils/compraInteligenteUtils";

const MIME_EXCEL_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
type CamposResumoProdutoVitrine = Pick<IVitrineAgrupamentoItem, "imagem_url" | "tem_anotacao">;

type IVitrineAgrupamentoItemSemResumoProduto = Omit<IVitrineAgrupamentoItem, keyof CamposResumoProdutoVitrine> &
	Partial<CamposResumoProdutoVitrine>;

type IVitrineAgrupamentoResponseSemResumoProduto = Omit<IVitrineAgrupamentoResponse, "itens"> & {
	itens: IVitrineAgrupamentoItemSemResumoProduto[];
};

type ResumoProdutoVitrine = {
	readonly imagemUrl: string | null;
	readonly temAnotacao: boolean;
};

// ------------------------------------------ HELPERS -----------------------------------------------------
function normalizarCodigoProdutoVitrine(produtoCodigo: unknown): number | null {
	const codigoNormalizado = Number(produtoCodigo);
	return Number.isFinite(codigoNormalizado) ? codigoNormalizado : null;
}

function normalizarQuantidadeCoresProdutoVitrine(quantidadeCores: unknown): number | null {
	const quantidadeNormalizada = Number(quantidadeCores);
	return Number.isFinite(quantidadeNormalizada) ? quantidadeNormalizada : null;
}

function normalizarDescricaoProdutoVitrine(descricao: unknown): string | null {
	if (typeof descricao !== "string") return null;

	const descricaoNormalizada = descricao.trim();
	return descricaoNormalizada || null;
}

function validarContextoConsultaVitrine(payload: IVitrineAgrupamentoPayload): string {
	const idRelatorioNormalizado = String(payload.id_relatorio ?? "")
		.trim()
		.toUpperCase();
	const organizacao = payload.organizacao ?? "AGRUPAMENTO";

	if (!idRelatorioNormalizado) throw new Error("Relatório inválido para consultar a vitrine.");

	if (organizacao === "AGRUPAMENTO" && !payload.agrupamento.length)
		throw new Error("Agrupamento inválido para consultar a vitrine.");

	if (organizacao === "FAIXA_PRECO") {
		if (payload.agrupamento.length)
			throw new Error("A vitrine Top produtos por faixa de preço não aceita agrupamento.");
		if (payload.top_n?.agrupar_por !== "produto")
			throw new Error("Top N por produto é obrigatório para consultar a vitrine por faixa de preço.");
		if (payload.grupo_principal || payload.sub_item)
			throw new Error("Grupo e subitem não são permitidos na vitrine por faixa de preço.");
	}

	return idRelatorioNormalizado;
}

function obterFiltrosItensVitrine(payload: IVitrineAgrupamentoPayload): IVitrineAgrupamentoPayload["filtros"] {
	if (payload.organizacao !== "FAIXA_PRECO") return payload.filtros;

	const faixaPreco = payload.filtros?.faixa_preco;
	if (!faixaPreco) throw new Error("Faixa de preço é obrigatória para consultar os itens da vitrine Top produtos.");

	return { faixa_preco: faixaPreco };
}

function obterPrimeiraUrlImagemProduto(urls: readonly string[] | undefined): string | null {
	const primeiraUrlValida = urls?.find((url) => typeof url === "string" && url.trim());
	return primeiraUrlValida?.trim() ?? null;
}

async function recuperarResumoProdutoVitrine(produtoCodigo: number, signal?: AbortSignal) {
	try {
		const resumoProduto = await getResumoProduto(produtoCodigo, signal);
		return [
			produtoCodigo,
			{
				imagemUrl: obterPrimeiraUrlImagemProduto(resumoProduto.urls),
				temAnotacao: resumoProduto.tem_anotacao === true,
			},
		] as const;
	} catch (error) {
		if (signal?.aborted || getStatusError(error)) throw error;
		return [produtoCodigo, { imagemUrl: null, temAnotacao: false }] as const;
	}
}

async function enriquecerItensVitrineComResumoProduto(
	resposta: IVitrineAgrupamentoResponseSemResumoProduto,
	signal?: AbortSignal,
): Promise<IVitrineAgrupamentoResponse> {
	const codigosProduto = Array.from(
		new Set(
			resposta.itens
				.map((item) => normalizarCodigoProdutoVitrine(item.produto_codigo))
				.filter((produtoCodigo): produtoCodigo is number => produtoCodigo !== null),
		),
	);

	const resumoPorProduto =
		codigosProduto.length > 0
			? new Map(
					await Promise.all(
						codigosProduto.map((produtoCodigo) => recuperarResumoProdutoVitrine(produtoCodigo, signal)),
					),
				)
			: new Map<number, ResumoProdutoVitrine>();

	return {
		...resposta,
		itens: resposta.itens.map((item) => {
			const produtoCodigo = normalizarCodigoProdutoVitrine(item.produto_codigo);
			const produtoAgrupamento = normalizarCodigoProdutoVitrine(item.produto_agrupamento) ?? produtoCodigo;
			const quantidadeCores = normalizarQuantidadeCoresProdutoVitrine(item.quantidade_cores);
			const produtoDescricao = normalizarDescricaoProdutoVitrine(item.produto_descricao);
			const resumoProduto = produtoCodigo === null ? null : resumoPorProduto.get(produtoCodigo);

			return {
				...item,
				produto_agrupamento: produtoAgrupamento,
				quantidade_cores: quantidadeCores,
				produto_descricao: produtoDescricao,
				imagem_url: resumoProduto?.imagemUrl ?? null,
				tem_anotacao: resumoProduto?.temAnotacao ?? false,
			};
		}),
	};
}

// ----------------------------------------------- ANÁLISE DE ESTOQUE - CONSULTAR ------------------------------------
export async function analiseConsultar(payload: IInventoryAnalysisPayload) {
	return spaceApiCompraInteligente
		.post("analise-estoque/consultar", {
			json: payload,
		})
		.json<IAnaliseEstoqueRawRow[]>();
}

export async function analiseConsultarTopProdutos(payload: ITopProdutosPayload) {
	return spaceApiCompraInteligente
		.post("analise-estoque/consultar-top-produtos", {
			json: payload,
		})
		.json<IAnaliseEstoqueRawRow[]>();
}

export async function getProdutoInfo(payload: IProductInfoPayload) {
	const idRelatorioNormalizado = String(payload.id_relatorio).trim().toUpperCase();
	if (!idRelatorioNormalizado) throw new Error("Relatório inválido para consultar informações do produto.");

	return spaceApiCompraInteligente
		.post("analise-estoque/consultar-detalhe-produto", {
			json: {
				id_relatorio: idRelatorioNormalizado,
				produto_codigo: payload.produto_codigo,
			},
		})
		.json<IProductInfoResponse>();
}

// ----------------------------------------------- PRODUTO DISTRIBUIÇÃO ---------------------------------------------
export async function getProdutoDistribuicaoTable(
	payload: IProductDistributionPayload,
): Promise<IProductDistributionViewResponse> {
	return spaceApiCompraInteligente
		.post("analise-estoque/consultar-produto", {
			json: payload,
		})
		.json<IProductDistributionViewResponse>();
}

export async function getProdutoDistribuicaoCores(
	payload: IProductDistributionColorsPayload,
): Promise<IProductDistributionColorItemResponse[]> {
	return spaceApiCompraInteligente
		.post("analise-estoque/consultar-cores-produto", {
			json: payload,
		})
		.json<IProductDistributionColorItemResponse[]>();
}

// ----------------------------------------------- VITRINE AGRUPAMENTO -----------------------------------------------
async function consultarEstruturaVitrine(
	payload: IVitrineAgrupamentoPayload,
	idRelatorio: string,
	faixaPrecoValor: number,
	signal?: AbortSignal,
): Promise<IVitrineAgrupamentoResponse> {
	const payloadEstrutura: IVitrineAgrupamentoPayload = {
		id_relatorio: idRelatorio,
		agrupamento: [...payload.agrupamento],
		...(payload.organizacao ? { organizacao: payload.organizacao } : {}),
		...(payload.top_n ? { top_n: payload.top_n } : {}),
		faixa_preco_valor: faixaPrecoValor,
		...(payload.organizacao !== "FAIXA_PRECO" && payload.filtros ? { filtros: payload.filtros } : {}),
		modo: "ESTRUTURA",
	};

	const resposta = await spaceApiCompraInteligente
		.post("vitrine/recuperar-agrupamentos", {
			signal,
			json: payloadEstrutura,
		})
		.json<Partial<IVitrineAgrupamentoResponse>>();

	return normalizarRespostaVitrine(resposta);
}

async function consultarItensVitrine(
	payload: IVitrineAgrupamentoPayload,
	idRelatorio: string,
	faixaPrecoValor: number,
	signal?: AbortSignal,
): Promise<IVitrineAgrupamentoResponse> {
	const exigeSubItem = payload.agrupamento.length > 1;

	const organizacaoFaixaPreco = payload.organizacao === "FAIXA_PRECO";
	if (!organizacaoFaixaPreco && (!payload.grupo_principal || (exigeSubItem && !payload.sub_item)))
		throw new Error(
			exigeSubItem
				? "Grupo e subitem são obrigatórios para consultar os itens da vitrine."
				: "Grupo principal é obrigatório para consultar os itens da vitrine.",
		);
	const filtrosItens = obterFiltrosItensVitrine(payload);

	const payloadItens: IVitrineAgrupamentoPayload = {
		id_relatorio: idRelatorio,
		agrupamento: [...payload.agrupamento],
		...(payload.organizacao ? { organizacao: payload.organizacao } : {}),
		...(payload.top_n ? { top_n: payload.top_n } : {}),
		...(payload.grupo_principal ? { grupo_principal: payload.grupo_principal } : {}),
		...(payload.sub_item ? { sub_item: payload.sub_item } : {}),
		...(organizacaoFaixaPreco ? { faixa_preco_valor: faixaPrecoValor } : {}),
		...(filtrosItens ? { filtros: filtrosItens } : {}),
		cursor: payload.cursor ?? null,
		limit: payload.limit ?? 50,
		modo: "ITENS",
	};

	const resposta = await spaceApiCompraInteligente
		.post("vitrine/listar-itens", {
			signal,
			json: payloadItens,
		})
		.json<IVitrineAgrupamentoResponseSemResumoProduto>();

	return normalizarRespostaVitrine(await enriquecerItensVitrineComResumoProduto(resposta, signal));
}

export async function consultarVitrineAgrupamento(
	payload: IVitrineAgrupamentoPayload,
	signal?: AbortSignal,
): Promise<IVitrineAgrupamentoResponse> {
	const idRelatorio = validarContextoConsultaVitrine(payload);
	const faixaPrecoValor = normalizarFaixaPrecoValorVitrine(payload.faixa_preco_valor);

	if ((payload.modo ?? "ITENS") === "ESTRUTURA")
		return consultarEstruturaVitrine(payload, idRelatorio, faixaPrecoValor, signal);

	const limitRaw = Number(payload.limit ?? 50);
	const limit = Number.isFinite(limitRaw) && limitRaw > 0 ? Math.floor(limitRaw) : 50;
	return consultarItensVitrine({ ...payload, limit }, idRelatorio, faixaPrecoValor, signal);
}

export async function gerarPDFVitrine(payload: IVitrineAgrupamentoPayload) {
	const payloadPDF = { ...payload };
	delete payloadPDF.cursor;

	return spaceApiCompraInteligente
		.post("vitrine/gerar-pdf", {
			json: payloadPDF,
		})
		.blob();
}

// -------------------------------------------------------- MÉTRICAS DASHBOARD ----------------------------------------
export async function consultarMetricas(payload: IConsultarMetricasPayload, signal?: AbortSignal) {
	return spaceApiCompraInteligente
		.post("analise-estoque/consultar-metricas-gerais", {
			signal,
			json: payload,
		})
		.json<IRespostaMetricasDashboard>();
}

// -------------------------------------------------------- PLANEJAMENTO OTB ------------------------------------------
export async function recuperarResumoOTB(
	idRelatorio: string,
	agrupamento: readonly EAnaliseEstoqueRequestFieldKey[],
	agrupamentoValor?: IOTBAgrupamentoValor | null,
	signal?: AbortSignal,
): Promise<IResumoOTBResponse> {
	const idRelatorioNormalizado = String(idRelatorio ?? "")
		.trim()
		.toUpperCase();

	if (!idRelatorioNormalizado) throw new Error("Relatório inválido para recuperar OTB.");
	if (!agrupamento.length) throw new Error("Agrupamento inválido para recuperar OTB.");

	const agrupamentoValorValido =
		agrupamentoValor && Object.keys(agrupamentoValor).length ? agrupamentoValor : undefined;
	const payloadResumoOTB: IResumoOTBPayload = {
		id_relatorio: idRelatorioNormalizado,
		agrupamento: [...agrupamento],
		...(agrupamentoValorValido ? { agrupamento_valor: agrupamentoValorValido } : {}),
	};

	return spaceApiCompraInteligente
		.post("otb/recuperar-resumo", {
			signal,
			json: payloadResumoOTB,
		})
		.json<IResumoOTBResponse>();
}

export async function recuperarOTBGradeResumida(
	payload: IOTBGradeResumidaPayload,
	signal?: AbortSignal,
): Promise<IOTBGradeResumidaResponse> {
	const idRelatorioNormalizado = String(payload.id_relatorio ?? "")
		.trim()
		.toUpperCase();

	if (!idRelatorioNormalizado) throw new Error("Relatório inválido para recuperar grade resumida OTB.");
	if (!payload.agrupamento.length) throw new Error("Agrupamento inválido para recuperar grade resumida OTB.");

	return spaceApiCompraInteligente
		.post("otb/recuperar-grade-resumida", {
			signal,
			json: {
				...payload,
				id_relatorio: idRelatorioNormalizado,
			},
		})
		.json<IOTBGradeResumidaResponse>();
}

export async function recuperarOTBGradeProduto(
	payload: IProductDistributionPayload,
	signal?: AbortSignal,
): Promise<IOTBGradeProdutoResponse> {
	const idRelatorioNormalizado = String(payload.id_relatorio ?? "")
		.trim()
		.toUpperCase();

	if (!idRelatorioNormalizado) throw new Error("Relatório inválido para recuperar grade do produto OTB.");
	if (payload.produto_agrupamento === null || payload.produto_agrupamento === undefined)
		throw new Error("Produto inválido para recuperar grade do produto OTB.");

	return spaceApiCompraInteligente
		.post("otb/recuperar-grade-produto", {
			signal,
			json: {
				...payload,
				id_relatorio: idRelatorioNormalizado,
			},
		})
		.json<IOTBGradeProdutoResponse>();
}

// -------------------------------------------------------- RECUPERA SQL FUNCTIONS ------------------------------------
export async function recoverSQLQuery(reportId: string) {
	const reportIdTrimmed = reportId.trim().toUpperCase();

	const params = new URLSearchParams();
	params.append("id_relatorio", reportIdTrimmed);

	return spaceApiCompraInteligente
		.get("analise-estoque/recuperar-sql", {
			searchParams: params,
		})
		.text();
}

export async function recoverMongoQuery(payload: IInventoryAnalysisPayload) {
	if (!payload) throw new Error("Payload é obrigatório para recuperar a consulta MongoDB");

	const res = await spaceApiCompraInteligente
		.post("analise-estoque/recuperar-json-mongo-analise-estoque", {
			json: payload,
		})
		.text();
	return res;
}

export async function recoverQueries() {
	return spaceApiCompraInteligente.get("analise-estoque/recuperar-consultas").json<IQueryHistoryItemResponse[]>();
}

//------------------------------------------------- EXPORT EXCEL FUNCTIONS -----------------------------------------
export async function gerarExportExcel(payload: IGerarExportExcelPayload): Promise<IGerarExportExcelResponse> {
	const { id_relatorio, agrupar_por, top_n, ...filtros } = payload;

	const response = await spaceApiCompraInteligente.post("analise-estoque/gerar-excel-completo", {
		json: {
			...filtros,
			...(top_n ? { top_n } : {}),
			id_relatorio: String(id_relatorio ?? "")
				.trim()
				.toUpperCase(),
			agrupar_por,
		},
		throwHttpErrors: false,
	});

	const isStatusEsperado = response.status === 202 || response.status === 409;
	if (!isStatusEsperado) throw new Error(`${response.status}: ${response.statusText}`);

	return response.json<IGerarExportExcelResponse>();
}

export async function baixarExportExcel(payload: IDownloadExportExcelPayload): Promise<Blob> {
	const arquivoExcel = await spaceApiCompraInteligente
		.get("analise-estoque/download-excel-completo", {
			headers: {
				Accept: MIME_EXCEL_XLSX,
			},
			searchParams: {
				id_relatorio: payload.id_relatorio.trim().toUpperCase(),
				id_arquivo: payload.id_arquivo,
			},
		})
		.blob();
	return arquivoExcel;
}
